import { Button } from "@/components/ui/button";
import { Check, X, Phone, Mail, Globe, MapPin, Linkedin, Twitter, Clock, Users, TrendingDown, Search, UserPlus, Star } from "lucide-react";

const HomepageVanishingAct = () => {
  const stats = [
    { icon: Clock, value: "48hrs", label: "Average time before a business card is lost", color: "text-red-400" },
    { icon: TrendingDown, value: "88%", label: "Of paper cards discarded within a week", color: "text-orange-400" },
    { icon: Users, value: "90%", label: "Of contacts never followed up", color: "text-yellow-400" },
  ];

  const features = [
    "Your photo & professional bio",
    "Searchable keywords they can find you by",
    "All your contact details in one tap",
    "Social links & portfolio",
    "Completely free forever",
  ];

  const testimonials = [
    { name: "Sarah Chen", role: "Sales Director", quote: "I used to wonder why leads went cold. Now I know — they just couldn't find me. QCard changed that." },
    { name: "Marcus Johnson", role: "Real Estate Agent", quote: "Every client I meet now has my contact saved instantly. My callback rate doubled." },
    { name: "Emily Rodriguez", role: "Consultant", quote: "No more 'Who was that person again?' moments. QCard makes me unforgettable." },
  ];

  const comparisons = [
    { feature: "Saved to Contacts", paper: false, qcard: true },
    { feature: "Photo & Bio Included", paper: false, qcard: true },
    { feature: "Searchable by Keywords", paper: false, qcard: true },
    { feature: "Always Up-to-Date", paper: false, qcard: true },
    { feature: "Zero Cost", paper: false, qcard: true },
    { feature: "Eco-Friendly", paper: false, qcard: true },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Spotlight effect */}
        <div className="absolute inset-0 bg-gradient-to-b from-red-900/20 via-transparent to-transparent" />
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-red-500/10 rounded-full blur-[120px]" />
        
        <div className="container mx-auto px-4 py-20 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/10 border border-red-500/20 rounded-full text-red-400 text-sm">
                <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                The Vanishing Act
              </div>
              
              <h1 className="text-5xl md:text-7xl font-bold leading-tight">
                Your contact details
                <span className="block text-red-400">shouldn't disappear</span>
                the moment the conversation ends.
              </h1>
              
              <p className="text-xl text-gray-400 max-w-lg">
                Most people you meet forget your name, your face, or what you do — not because they don't care, but because they never saved your information.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" className="bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white px-8 py-6 text-lg rounded-full">
                  Get Your Free QCard
                </Button>
                <Button size="lg" variant="outline" className="border-gray-700 text-white hover:bg-white/5 px-8 py-6 text-lg rounded-full">
                  See How It Works
                </Button>
              </div>
            </div>
            
            {/* Visual: Vanishing card effect */}
            <div className="relative flex justify-center">
              <div className="relative">
                {/* Fading paper card */}
                <div className="absolute -left-20 top-20 w-64 h-40 bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg border border-gray-700 opacity-30 rotate-[-15deg] blur-sm">
                  <div className="p-4 space-y-2">
                    <div className="w-20 h-3 bg-gray-600 rounded" />
                    <div className="w-32 h-2 bg-gray-700 rounded" />
                  </div>
                </div>
                
                {/* Phone with QCard */}
                <div className="relative w-72 h-[580px] bg-gradient-to-b from-gray-800 to-gray-900 rounded-[3rem] border-4 border-gray-700 shadow-2xl shadow-red-500/20">
                  <div className="absolute top-4 left-1/2 -translate-x-1/2 w-24 h-6 bg-black rounded-full" />
                  <div className="absolute inset-4 top-12 bg-[#0f0f15] rounded-[2rem] overflow-hidden">
                    <div className="p-6 space-y-4">
                      {/* Contact saved notification */}
                      <div className="bg-green-500/20 border border-green-500/30 rounded-xl p-3 flex items-center gap-3">
                        <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                          <Check className="w-5 h-5 text-white" />
                        </div>
                        <span className="text-green-400 text-sm font-medium">Contact Saved!</span>
                      </div>
                      
                      {/* Profile */}
                      <div className="text-center space-y-3 pt-4">
                        <div className="w-20 h-20 mx-auto bg-gradient-to-br from-red-400 to-orange-500 rounded-full flex items-center justify-center text-3xl font-bold">
                          JD
                        </div>
                        <div>
                          <h3 className="text-xl font-bold">John Doe</h3>
                          <p className="text-gray-400 text-sm">CEO at TechCorp</p>
                        </div>
                      </div>
                      
                      {/* Contact details */}
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-3 p-2 bg-white/5 rounded-lg">
                          <Phone className="w-4 h-4 text-red-400" />
                          <span className="text-gray-300">+1 (555) 123-4567</span>
                        </div>
                        <div className="flex items-center gap-3 p-2 bg-white/5 rounded-lg">
                          <Mail className="w-4 h-4 text-red-400" />
                          <span className="text-gray-300">john@techcorp.com</span>
                        </div>
                        <div className="flex items-center gap-3 p-2 bg-white/5 rounded-lg">
                          <Search className="w-4 h-4 text-red-400" />
                          <span className="text-gray-400 text-xs">tech, startup, investor</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Glow effect */}
                <div className="absolute inset-0 -z-10 bg-gradient-to-r from-red-500/30 to-orange-500/30 blur-3xl rounded-full scale-150 opacity-50" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Stats Section */}
      <section className="py-24 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-red-950/10 to-transparent" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">The Problem Is Real</h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Paper cards get tossed. Links get lost. Even other digital cards fail because they don't add you into Contacts properly.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="bg-gradient-to-b from-gray-800/50 to-gray-900/50 border border-gray-800 rounded-2xl p-8 text-center hover:border-red-500/50 transition-colors">
                <stat.icon className={`w-12 h-12 mx-auto mb-4 ${stat.color}`} />
                <div className={`text-5xl font-bold mb-2 ${stat.color}`}>{stat.value}</div>
                <p className="text-gray-400">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section className="py-24 bg-gradient-to-b from-[#0a0a0f] via-[#100a0a] to-[#0a0a0f]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-8">The Impact of Vanishing</h2>
            <div className="space-y-6 text-xl text-gray-300">
              <p>Opportunities vanish. Follow-ups never happen.</p>
              <p className="text-red-400 font-semibold text-2xl">
                Business that should have been yours goes to someone else simply because you weren't in their Contacts app.
              </p>
              <p>You're not even a memory — you're invisible.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <h2 className="text-4xl md:text-5xl font-bold">
                QCard Makes You
                <span className="text-red-400"> Permanent</span>
              </h2>
              <p className="text-xl text-gray-400">
                QCard makes sure you're instantly saved into someone's phone — photo, name, contact info, links, and even keywords so they can search for you later.
              </p>
              
              <div className="space-y-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-4 p-4 bg-gradient-to-r from-red-500/10 to-transparent border-l-2 border-red-500 rounded-r-lg">
                    <Check className="w-6 h-6 text-red-400 flex-shrink-0" />
                    <span className="text-lg">{feature}</span>
                  </div>
                ))}
              </div>
              
              <Button size="lg" className="bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white px-8 py-6 text-lg rounded-full">
                Get Your Free QCard
              </Button>
            </div>
            
            {/* Add to Contacts Demo */}
            <div className="relative">
              <div className="bg-gradient-to-b from-gray-800/50 to-gray-900/50 border border-gray-800 rounded-3xl p-8">
                <div className="text-center mb-6">
                  <UserPlus className="w-16 h-16 mx-auto text-red-400 mb-4" />
                  <h3 className="text-2xl font-bold">One Tap. Saved Forever.</h3>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center gap-4 p-4 bg-white/5 rounded-xl">
                    <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center text-2xl">1</div>
                    <div>
                      <p className="font-semibold">They tap your QCard link</p>
                      <p className="text-sm text-gray-400">Shared via text, email, or NFC</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-4 bg-white/5 rounded-xl">
                    <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center text-2xl">2</div>
                    <div>
                      <p className="font-semibold">Click "Add to Contacts"</p>
                      <p className="text-sm text-gray-400">One button, no typing required</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-4 bg-green-500/10 rounded-xl border border-green-500/30">
                    <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center text-2xl">✓</div>
                    <div>
                      <p className="font-semibold text-green-400">You're saved permanently</p>
                      <p className="text-sm text-gray-400">Photo, info, links — all there</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-24 bg-gradient-to-b from-[#0a0a0f] via-[#0f0a0a] to-[#0a0a0f]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Paper Cards vs QCard</h2>
            <p className="text-xl text-gray-400">The difference is night and day</p>
          </div>
          
          <div className="max-w-3xl mx-auto">
            <div className="bg-gradient-to-b from-gray-800/30 to-gray-900/30 border border-gray-800 rounded-2xl overflow-hidden">
              <div className="grid grid-cols-3 gap-4 p-4 bg-gray-800/50 font-semibold">
                <div>Feature</div>
                <div className="text-center text-red-400">Paper Card</div>
                <div className="text-center text-green-400">QCard</div>
              </div>
              {comparisons.map((item, index) => (
                <div key={index} className="grid grid-cols-3 gap-4 p-4 border-t border-gray-800/50 hover:bg-white/5 transition-colors">
                  <div className="text-gray-300">{item.feature}</div>
                  <div className="text-center">
                    <X className="w-6 h-6 text-red-500 mx-auto" />
                  </div>
                  <div className="text-center">
                    <Check className="w-6 h-6 text-green-500 mx-auto" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Free Tier Section */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block px-6 py-2 bg-green-500/10 border border-green-500/30 rounded-full text-green-400 mb-8">
              100% Free Forever
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">No Excuses. No Credit Card.</h2>
            <p className="text-xl text-gray-400 mb-12">
              QCard's core features are completely free. Create your professional digital identity in 60 seconds.
            </p>
            
            <div className="grid md:grid-cols-3 gap-6">
              {["Professional Profile", "Add to Contacts Button", "Unlimited Shares"].map((feature, index) => (
                <div key={index} className="p-6 bg-gradient-to-b from-gray-800/50 to-gray-900/50 border border-gray-800 rounded-xl">
                  <Check className="w-10 h-10 text-green-400 mx-auto mb-4" />
                  <p className="font-semibold">{feature}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-gradient-to-b from-[#0a0a0f] via-[#100a0a] to-[#0a0a0f]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">They Stopped Vanishing</h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="p-8 bg-gradient-to-b from-gray-800/50 to-gray-900/50 border border-gray-800 rounded-2xl">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                  ))}
                </div>
                <p className="text-lg text-gray-300 mb-6">"{testimonial.quote}"</p>
                <div>
                  <p className="font-semibold">{testimonial.name}</p>
                  <p className="text-sm text-gray-400">{testimonial.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-6xl font-bold mb-6">
              Stop Vanishing.
              <span className="text-red-400"> Start Connecting.</span>
            </h2>
            <p className="text-xl text-gray-400 mb-12">
              Every connection becomes permanent, searchable, and actionable.
            </p>
            <Button size="lg" className="bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white px-12 py-8 text-xl rounded-full">
              Get Your Free QCard Now
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-gray-800">
        <div className="container mx-auto px-4 text-center text-gray-400">
          <p>© 2024 QCard. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default HomepageVanishingAct;